import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Radar, Building2, Plus, Scale, Hammer, ShieldCheck, Handshake, Sparkles, Users, Megaphone, Star, Network, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BrazilMapHolo } from '@/components/map/BrazilMapHolo';
import { StatePanel } from '@/components/map/StatePanel';
import { ExploreFilters } from '@/components/explore/ExploreFilters';
import { CompanyCard, OpportunityCard } from '@/components/cards/Cards';
import { SectionHeader, EmptyState, DemoBadge } from '@/components/common/Primitives';
import { useData } from '@/hooks/useData';
import { useIsVisitor, VisitorOnly } from '@/hooks/useIsVisitor';
import { useMediaQuery, useReducedMotionPref } from '@/hooks/useMediaQuery';
import { useSeo } from '@/lib/seo';
import { filterCompanies, filterOpportunities, sortCompanies, sortOpportunities, filtersToParams } from '@/lib/filters';
import { OPPORTUNITY_TYPES } from '@/data/taxonomy';
import { cn } from '@/lib/utils';

const EMPTY = { uf: '', city: '', categoryId: '', businessType: '', q: '' };

const Reveal = ({ children, className, delay = 0 }) => {
  const reduced = useReducedMotionPref();
  return (
    <motion.div className={className} initial={reduced ? false : { opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.45, delay }}>
      {children}
    </motion.div>
  );
};

const HeroCopy = ({ stats }) => (
  <div className="flex h-full flex-col justify-center">
    <div className="eyebrow flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_#22d3ee]" /> Rede nacional · livre iniciativa</div>
    <h1 className="mt-4 text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl xl:text-[3.6rem]" data-testid="hero-title">
      Negócios fortes.<br />
      <span className="bg-[linear-gradient(90deg,#e6fbff,#67e8f9_60%,#3b82f6)] bg-clip-text text-transparent">Conexões que geram oportunidades.</span>
    </h1>
    <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-[17px]">
      Descubra empresas, encontre parceiros comerciais e divulgue seu negócio em uma rede nacional de empreendedores que valorizam a livre iniciativa e a liberdade econômica.
    </p>
    <div className="mt-7 flex flex-col gap-3 sm:flex-row">
      <Button asChild size="lg" className="btn-glow h-11 px-5" data-testid="hero-primary-cta">
        <Link to="/oportunidades"><Radar /> Explorar oportunidades</Link>
      </Button>
      <Button asChild size="lg" variant="secondary" className="h-11 border border-white/10 px-5" data-testid="hero-secondary-cta">
        <Link to="/cadastrar-empresa"><Plus /> Cadastrar minha empresa</Link>
      </Button>
    </div>
    <div className="mt-9">
      <VisitorOnly>
        <div className="mb-2 flex items-center gap-2">
          <DemoBadge label="Indicadores de demonstração" />
        </div>
      </VisitorOnly>
      <dl className="grid grid-cols-3 divide-x divide-white/10 rounded-xl border border-white/10 bg-[#0a0d13]" data-testid="hero-kpis">
        {stats.map((s) => (
          <div key={s.label} className="px-3 py-3 sm:px-4" data-testid={`hero-kpi-${s.id}`}>
            <dt className="text-[11px] leading-tight text-muted-foreground">{s.label}</dt>
            <dd className="mt-1 font-display text-2xl font-semibold tabular-nums sm:text-3xl">{s.value}</dd>
          </div>
        ))}
      </dl>
      <VisitorOnly>
        <p className="mt-2 text-[11px] text-muted-foreground">Números calculados a partir dos dados fictícios deste protótipo — não são estatísticas reais da plataforma.</p>
      </VisitorOnly>
    </div>
  </div>
);

const VALUES = [
  { icon: Scale, title: 'Liberdade econômica', text: 'Mercado aberto, concorrência leal e menos barreiras para empreender.' },
  { icon: Hammer, title: 'Valorização do trabalho', text: 'Reconhecimento de quem produz, emprega e gera valor.' },
  { icon: ShieldCheck, title: 'Responsabilidade individual', text: 'Compromisso com a palavra dada, contratos e reputação.' },
  { icon: Handshake, title: 'Cooperação comercial', text: 'Empresas que se fortalecem fazendo negócios entre si.' },
];

export default function Home() {
  useSeo({ title: 'Início' });
  const { publicCompanies, publicOpportunities, companyById, categoryById } = useData();
  const [uf, setUf] = useState(null);
  const [filters, setFilters] = useState(EMPTY);
  const [radarType, setRadarType] = useState('');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const isVisitor = useIsVisitor();

  const stats = useMemo(() => [
    { id: 'companies', label: 'Empresas cadastradas', value: publicCompanies.length },
    { id: 'opportunities', label: 'Oportunidades publicadas', value: publicOpportunities.length },
    { id: 'states', label: 'Estados com presença', value: new Set(publicCompanies.map((c) => c.location.uf)).size },
  ], [publicCompanies, publicOpportunities]);

  const selectUf = (next) => {
    setUf(next);
    if (next && !isDesktop) setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };

  const exploreCompanies = useMemo(() => sortCompanies(filterCompanies(publicCompanies, filters, categoryById)), [publicCompanies, filters, categoryById]);
  const exploreOpps = useMemo(() => sortOpportunities(filterOpportunities(publicOpportunities, filters, companyById), 'recentes'), [publicOpportunities, filters, companyById]);
  const featured = useMemo(() => sortCompanies(publicCompanies.filter((c) => c.featured || c.sponsored || !c.isDemo)).slice(0, 8), [publicCompanies]);
  const radar = useMemo(() => sortOpportunities(publicOpportunities.filter((o) => !radarType || o.type === radarType), 'recentes').slice(0, 6), [publicOpportunities, radarType]);
  const qs = new URLSearchParams(filtersToParams(filters)).toString();

  return (
    <div>
      {/* HERO + MAPA */}
      <section className="relative overflow-hidden border-b border-white/5" aria-labelledby="hero-title">
        <div className="pointer-events-none absolute inset-0 hero-glow" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 grid-floor opacity-30 [mask-image:linear-gradient(to_top,black,transparent)]" />
        <div className="container-x relative grid gap-8 py-10 lg:grid-cols-12 lg:gap-10 lg:py-14">
          <div className="lg:col-span-5">
            <AnimatePresence mode="wait" initial={false}>
              {isDesktop && uf ? (
                <motion.div key="panel" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }} className="h-full">
                  <StatePanel uf={uf} onSelectUf={selectUf} />
                </motion.div>
              ) : (
                <motion.div key="copy" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="h-full">
                  <HeroCopy stats={stats} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="lg:col-span-7">
            <BrazilMapHolo companies={publicCompanies} opportunities={publicOpportunities} selectedUf={uf} onSelectUf={selectUf} />
            <p className="mt-2 text-center text-xs text-muted-foreground">
              {isVisitor ? 'Toque ou clique em um estado para ver empresas e oportunidades de exemplo.' : 'Toque ou clique em um estado para ver empresas e oportunidades.'}
              <span className="hidden md:inline"> · Ctrl + rolagem para zoom · arraste após aproximar</span>
            </p>
          </div>
          {!isDesktop && (
            <div ref={panelRef} className="scroll-mt-20">
              <StatePanel uf={uf} onSelectUf={selectUf} />
            </div>
          )}
        </div>
      </section>

      {/* VALORES */}
      <section className="border-b border-white/5 bg-[#06080c]" aria-label="Valores">
        <div className="container-x grid grid-cols-1 gap-px overflow-hidden sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map(({ icon: Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 0.05} className="flex gap-3 px-1 py-6 sm:px-4">
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <div className="font-display text-sm font-semibold">{title}</div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EXPLORE */}
      <section className="container-x py-14 sm:py-20" id="explorar" aria-labelledby="explore-title">
        <SectionHeader id="explore-title" eyebrow="Diretório · filtros combináveis" title="Explore o ecossistema empresarial" text="Combine estado, cidade, categoria, tipo de negócio e palavra-chave para encontrar fornecedores, parceiros e clientes." />
        <ExploreFilters filters={filters} onChange={setFilters} onClear={() => setFilters(EMPTY)} testPrefix="explore" />
        <Tabs defaultValue="empresas" className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList className="border border-white/10 bg-[#0b0f16]">
              <TabsTrigger value="empresas" data-testid="explore-tab-companies">Empresas <span className="ml-1.5 font-mono text-[11px] opacity-70">{exploreCompanies.length}</span></TabsTrigger>
              <TabsTrigger value="oportunidades" data-testid="explore-tab-opportunities">Oportunidades <span className="ml-1.5 font-mono text-[11px] opacity-70">{exploreOpps.length}</span></TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(`/empresas${qs ? `?${qs}` : ''}`)} data-testid="explore-see-all-companies">Todas as empresas <ArrowRight /></Button>
              <Button variant="ghost" size="sm" onClick={() => navigate(`/oportunidades${qs ? `?${qs}` : ''}`)} data-testid="explore-see-all-opportunities">Todas as oportunidades <ArrowRight /></Button>
            </div>
          </div>
          <TabsContent value="empresas" className="mt-4">
            {exploreCompanies.length ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="explore-company-results">
                {exploreCompanies.slice(0, 6).map((c) => <CompanyCard key={c.id} company={c} />)}
              </div>
            ) : <EmptyState icon={Building2} title="Nenhuma empresa com esses filtros" text="Tente remover algum filtro ou buscar por outro termo." action="Limpar filtros" onAction={() => setFilters(EMPTY)} testId="explore-companies-empty" />}
          </TabsContent>
          <TabsContent value="oportunidades" className="mt-4">
            {exploreOpps.length ? (
              <div className="grid gap-3" data-testid="explore-opportunity-results">
                {exploreOpps.slice(0, 5).map((o) => <OpportunityCard key={o.id} opp={o} />)}
              </div>
            ) : <EmptyState icon={Radar} title="Nenhuma oportunidade com esses filtros" text="Ajuste os filtros para ver mais publicações." action="Limpar filtros" onAction={() => setFilters(EMPTY)} testId="explore-opportunities-empty" />}
          </TabsContent>
        </Tabs>
      </section>

      {/* EMPRESAS EM DESTAQUE */}
      <section className="border-y border-white/5 bg-[#06080c] py-14 sm:py-20" aria-labelledby="featured-title">
        <div className="container-x">
          <SectionHeader
            id="featured-title"
            eyebrow="Vitrine"
            title="Empresas em destaque"
            text={isVisitor
              ? 'Perfis de exemplo para visualizar a vitrine. Empresas fictícias — nenhum selo de verificação foi concedido.'
              : 'Empresas da rede em destaque para quem busca fornecedores, parceiros e clientes.'}
            right={<Button asChild variant="secondary" className="border border-white/10" data-testid="featured-see-all"><Link to="/empresas">Ver diretório completo <ArrowRight /></Link></Button>}
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="featured-companies">
            {featured.map((c, i) => <Reveal key={c.id} delay={(i % 4) * 0.05}><CompanyCard company={c} /></Reveal>)}
          </div>
        </div>
      </section>

      {/* RADAR */}
      <section className="container-x py-14 sm:py-20" aria-labelledby="radar-title">
        <SectionHeader
          id="radar-title"
          eyebrow={isVisitor ? 'Tempo real (demonstração)' : 'Tempo real'}
          title="Radar de oportunidades"
          text="Fornecedores, distribuidores, representantes, parcerias e mais. Publicações dos últimos 7 dias aparecem destacadas em vermelho."
          right={<Button asChild className="btn-glow" data-testid="radar-see-all"><Link to="/oportunidades"><Radar /> Abrir radar completo</Link></Button>}
        />
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1 scrollbar-thin" role="tablist" aria-label="Tipo de oportunidade">
          {[{ id: '', short: 'Todas' }, ...OPPORTUNITY_TYPES].map((t) => (
            <button key={t.id || 'all'} type="button" role="tab" aria-selected={radarType === t.id} onClick={() => setRadarType(t.id)} data-testid={`radar-type-${t.id || 'all'}`} className={cn('shrink-0 rounded-md border px-3 py-1.5 text-xs transition-colors', radarType === t.id ? 'border-cyan-300/40 bg-cyan-300/10 text-cyan-100' : 'border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground')}>
              {t.short}
            </button>
          ))}
        </div>
        {radar.length ? (
          <div className="grid gap-3 lg:grid-cols-2" data-testid="radar-list">
            {radar.map((o) => <OpportunityCard key={o.id} opp={o} />)}
          </div>
        ) : <EmptyState icon={Radar} title="Nenhuma oportunidade deste tipo" text="Seja o primeiro a publicar." action="Publicar oportunidade" onAction={() => navigate('/painel/publicar')} />}
      </section>

      {/* COMUNIDADE (prévia) */}
      <section className="border-y border-white/5 bg-[#06080c] py-14 sm:py-20" aria-labelledby="community-title">
        <div className="container-x">
          <SectionHeader id="community-title" eyebrow="Em construção" title="Comunidade e indicações" text="Uma prévia do que virá: recomendações entre empresas, novas conexões e destaques. Nesta versão, o foco é o diretório e as oportunidades." right={<Button asChild variant="secondary" className="border border-white/10"><Link to="/comunidade">Ver prévia <ArrowRight /></Link></Button>} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { icon: Star, t: 'Empresas recomendadas' },
              { icon: Network, t: 'Novas conexões comerciais' },
              { icon: Sparkles, t: 'Destaques empresariais' },
              { icon: Users, t: 'Indicações de prestadores' },
              { icon: Megaphone, t: 'Publicações institucionais' },
            ].map(({ icon: Icon, t }) => (
              <div key={t} className="surface flex items-center gap-3 p-4">
                <Icon className="h-4 w-4 text-primary" />
                <span className="flex-1 text-sm">{t}</span>
                <span className="rounded border border-white/10 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">EM BREVE</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section className="container-x py-14 sm:py-20" aria-labelledby="plans-title">
        <SectionHeader
          id="plans-title"
          eyebrow="Modelo em definição"
          title="Formas de participar"
          text={isVisitor
            ? 'Preços e condições ainda serão definidos. Nenhum pagamento é processado nesta demonstração.'
            : 'Preços e condições ainda serão definidos. Nenhum pagamento é processado no momento.'}
        />
        <div className="grid gap-4 md:grid-cols-3" data-testid="plans">
          {[
            { name: 'Perfil gratuito', tag: isVisitor ? 'Disponível na demo' : 'Disponível agora', items: ['Perfil empresarial completo', 'Publicação de oportunidades', 'Presença no mapa'], cta: 'Cadastrar empresa', to: '/cadastrar-empresa', hl: false },
            { name: 'Perfil premium', tag: 'Futuro · preço a definir', items: ['Maior exposição nas buscas', 'Destaque no mapa e na vitrine', 'Mais itens de portfólio'], cta: 'Saiba mais', to: '/publicidade', hl: true },
            { name: 'Publicações patrocinadas', tag: 'Futuro · preço a definir', items: ['Oportunidades em destaque no radar', 'Sempre identificadas como “Patrocinado”', 'Segmentação por estado e categoria'], cta: 'Saiba mais', to: '/publicidade', hl: false },
          ].map((p) => (
            <div key={p.name} className={cn('surface relative flex flex-col p-5', p.hl && 'border-cyan-300/25 shadow-[0_0_0_1px_rgba(34,211,238,0.15),0_18px_60px_rgba(0,0,0,0.55)]')} data-testid={`plan-${p.name}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{p.tag}</div>
              <div className="mt-2 font-display text-lg font-semibold">{p.name}</div>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {p.items.map((i) => <li key={i} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />{i}</li>)}
              </ul>
              <Button asChild variant={p.hl ? 'default' : 'secondary'} className={cn('mt-6 border border-white/10', p.hl && 'btn-glow')}><Link to={p.to}>{p.cta}</Link></Button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-x">
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0a0e15] px-6 py-10 sm:px-10">
          <div className="pointer-events-none absolute inset-0 grid-floor opacity-40 [mask-image:radial-gradient(ellipse_at_right,black,transparent_70%)]" />
          <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Coloque sua empresa no mapa.</h2>
              <p className="mt-2 max-w-xl text-sm text-muted-foreground">Cadastre seu negócio, publique oportunidades e seja encontrado por empresários de todo o Brasil.</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button asChild size="lg" className="btn-glow" data-testid="cta-register"><Link to="/cadastrar-empresa"><Plus /> Cadastrar empresa</Link></Button>
              <Button asChild size="lg" variant="secondary" className="border border-white/10"><Link to="/sobre"><Lock /> Como protegemos a rede</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}